import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import GroupIcon from "@mui/icons-material/Group";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import NomineeDocuments from "./NomineeDocuments";
import {
  API_BASE_URL,
  PRIMARY,
  DEFAULT_PAWN_DOCUMENTS,
  initialNomineeState,
  mobileFieldProps,
  sanitizeMobileInput,
} from "./shared";

// ─── NomineeDialog ────────────────────────────────────────────────────────────
// Used both in create mode (no API call — just builds local state)
// and edit mode (fires PUT immediately on "Save Nominee")
const NomineeDialog = ({
  open,
  onClose,
  mode, // 'create' | 'edit'
  initialData, // pre-filled for edit
  customerId, // needed for edit API call
  customerName, // shown in the dialog heading
  siblingNominees, // other nominees — used to enforce a single primary
  storeId,
  onSaved, // callback after successful save (refreshes parent)
  onLocalSave, // callback for create mode (just stores locally)
  onNomineeUpdated,
  showSnack,
}) => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [nominee, setNominee] = useState(initialNomineeState);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({ name: false, mobile: false });

  // Populate form when opening in edit mode or when initialData changes
  useEffect(() => {
    if (open) {
      if (initialData) {
        setNominee({
          name: initialData.name || "",
          mobile: initialData.mobile || "",
          dob: initialData.dob || "",
          pickupAddress: initialData.pickup_address || "",
          relationType:
            initialData.relative_relation === "None"
              ? ""
              : initialData.relative_relation || "",
          relationName: initialData.relative_name || "",
          annualIncome: initialData.annual_income?.toString() || "",
          note: initialData.note || "",
          partyCode: initialData.party_code || "",
          relation: initialData.relation || "",
          sharePercent: initialData.share_percent?.toString() || "",
          isPrimary: initialData.is_primary || false,
          documents: initialData.documents?.length
            ? initialData.documents.map((d) => ({
                doc_id: d.doc_id || null,
                name: d.name,
                number: d.number || "",
                file: d.file || null,
                fileName: d.fileName || "",
                renewalDate: d.renewalDate || "",
              }))
            : JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
        });
      } else {
        setNominee({
          ...initialNomineeState,
          documents: JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
        });
      }
      setErrors({ name: false, mobile: false });
    }
  }, [open, initialData]);

  const handleChange = (field, value) => {
    let processedValue = value;

    // Digits only, 10 at most. The field's own keystroke guard already refuses
    // anything else from a hardware keyboard; this catches paste and autofill.
    if (field === "mobile") processedValue = sanitizeMobileInput(value);

    setNominee((prev) => ({ ...prev, [field]: processedValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const buildDocsPayload = (docs) =>
    docs
      .filter((doc) => {
        if (doc.name === "Other") {
          return (doc.file && doc.file !== "No file") || doc.number?.trim();
        }
        return true;
      })
      .map((doc) => ({
        doc_id: doc.doc_id || null,
        name: doc.name,
        number: doc.number,
        file: doc.file,
        fileName: doc.fileName,
        renewalDate: doc.renewalDate || null,
      }));

  const validate = () => {
    const errs = {
      name: !nominee.name.trim(),
      mobile: !nominee.mobile.trim(),
    };
    setErrors(errs);
    return !Object.values(errs).some(Boolean);
  };

  const handleSave = async () => {
    if (!validate()) {
      showSnack("Name and Mobile are required for nominee", "error");
      return;
    }

    // ── CREATE mode: just pass data back to parent ──────────────────────────
    if (mode === "create") {
      onLocalSave({
        name: nominee.name,
        mobile: nominee.mobile,
        dob: nominee.dob || null,
        pickup_address: nominee.pickupAddress || null,
        relative_relation: nominee.relationType || "None",
        relative_name: nominee.relationName || null,
        annual_income: nominee.annualIncome
          ? parseFloat(nominee.annualIncome)
          : null,
        note: nominee.note || null,
        party_code: nominee.partyCode || null,
        relation: nominee.relation || null,
        share_percent: nominee.sharePercent
          ? parseFloat(nominee.sharePercent)
          : null,
        is_primary: nominee.isPrimary ? 1 : 0,
        documents: buildDocsPayload(nominee.documents),
        _snapshot: { ...nominee },
      });
      onClose();
      return;
    }

    // ── EDIT mode: fire PUT and optimistically update UI ──────────────────────
    setIsSaving(true);
    try {
      const isExisting = !!initialData?.link_id;

      // When this nominee is being set as primary, demote every OTHER nominee
      // that is currently primary so a customer keeps exactly one primary.
      // `documents` is intentionally omitted so the backend skips doc
      // reprocessing and only flips the junction's is_primary flag.
      const demotions = nominee.isPrimary
        ? (siblingNominees || [])
            .filter(
              (n) =>
                n.link_id &&
                n.link_id !== initialData?.link_id &&
                (n.is_primary === true || n.is_primary === 1),
            )
            .map((n) => ({
              link_id: n.link_id,
              nominee_id: n.nominee_id,
              relation: n.relation ?? null,
              share_percent:
                n.share_percent != null ? parseFloat(n.share_percent) : null,
              is_primary: 0,
              name: n.name,
              mobile: n.mobile,
              dob: n.dob || null,
              pickup_address: n.pickup_address || null,
              relative_relation: n.relative_relation || "None",
              relative_name: n.relative_name || null,
              annual_income: n.annual_income != null ? n.annual_income : null,
              note: n.note || null,
            }))
        : [];

      const payload = {
        store_id: storeId,
        ...(isExisting
          ? {
              nominees_to_update: [
                {
                  link_id: initialData.link_id,
                  nominee_id: initialData.nominee_id,
                  relation: nominee.relation || null,
                  share_percent: nominee.sharePercent
                    ? parseFloat(nominee.sharePercent)
                    : null,
                  is_primary: nominee.isPrimary ? 1 : 0,
                  name: nominee.name,
                  mobile: nominee.mobile,
                  dob: nominee.dob || null,
                  pickup_address: nominee.pickupAddress || null,
                  relative_relation: nominee.relationType || "None",
                  relative_name: nominee.relationName || null,
                  annual_income: nominee.annualIncome
                    ? parseFloat(nominee.annualIncome)
                    : null,
                  note: nominee.note || null,
                  documents: buildDocsPayload(nominee.documents),
                },
                ...demotions,
              ],
            }
          : {
              nominees_to_add: [
                {
                  name: nominee.name,
                  mobile: nominee.mobile,
                  dob: nominee.dob || null,
                  pickup_address: nominee.pickupAddress || null,
                  relative_relation: nominee.relationType || "None",
                  relative_name: nominee.relationName || null,
                  annual_income: nominee.annualIncome
                    ? parseFloat(nominee.annualIncome)
                    : null,
                  note: nominee.note || null,
                  party_code: nominee.partyCode || null,
                  relation: nominee.relation || null,
                  share_percent: nominee.sharePercent
                    ? parseFloat(nominee.sharePercent)
                    : null,
                  is_primary: nominee.isPrimary ? 1 : 0,
                  documents: buildDocsPayload(nominee.documents),
                },
              ],
              ...(demotions.length ? { nominees_to_update: demotions } : {}),
            }),
      };

      const res = await axios.put(
        `${API_BASE_URL}/api/pawn/customers/${customerId}`,
        payload,
      );
      if (!res.data.success) throw res.data;

      showSnack(
        res.data.statusMessage || "Nominee updated successfully",
        "success",
      );

      // ── Optimistic UI update: notify parent to update the nominee in state ──
      if (onNomineeUpdated && isExisting) {
        const updatedNominee = {
          link_id: initialData.link_id,
          nominee_id: initialData.nominee_id,
          name: nominee.name,
          mobile: nominee.mobile,
          dob: nominee.dob || null,
          pickup_address: nominee.pickupAddress || null,
          relative_relation: nominee.relationType || "None",
          relative_name: nominee.relationName || null,
          annual_income: nominee.annualIncome
            ? parseFloat(nominee.annualIncome)
            : null,
          note: nominee.note || null,
          party_code: nominee.partyCode || null,
          relation: nominee.relation || null,
          share_percent: nominee.sharePercent
            ? parseFloat(nominee.sharePercent)
            : null,
          is_primary: nominee.isPrimary ? 1 : 0,
          documents: buildDocsPayload(nominee.documents),
        };
        onNomineeUpdated(updatedNominee);
      }

      await onSaved(); // background refresh (optional, keeps consistency)
      onClose();
    } catch (error) {
      console.error("Nominee save error:", error);
      showSnack(error?.statusMessage || "Failed to save nominee", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const isEditing =
    mode === "create" ? !!initialData : !!initialData?.link_id;
  const customerLabel = customerName?.trim();
  const dialogTitle = isEditing
    ? `Save Nominee${customerLabel ? ` for ${customerLabel}` : ""}`
    : `Add Nominee${customerLabel ? ` to ${customerLabel}` : ""}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(90deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)`,
          color: "#fff",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1.5,
        }}
      >
        <GroupIcon />
        <Typography
          component="span"
          noWrap
          sx={{ minWidth: 0, flex: 1, fontWeight: 700 }}
        >
          {dialogTitle}
        </Typography>
        {isSaving && <CircularProgress size={18} sx={{ color: "#fff" }} />}
        <Button
          size={isMobile ? "small" : "medium"}
          onClick={() => handleChange("isPrimary", !nominee.isPrimary)}
          startIcon={nominee.isPrimary ? <StarIcon /> : <StarBorderIcon />}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            whiteSpace: "nowrap",
            color: "#fff",
            backgroundColor: nominee.isPrimary
              ? "rgba(249,168,37,0.9)"
              : "rgba(255,255,255,0.16)",
            border: "1px solid rgba(255,255,255,0.4)",
            "&:hover": {
              backgroundColor: nominee.isPrimary
                ? "rgba(249,168,37,1)"
                : "rgba(255,255,255,0.28)",
            },
          }}
        >
          {nominee.isPrimary ? "Primary Nominee" : "Set as Primary"}
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Stack spacing={2}>
          {/* ── Nominee personal details (relationship info merged in) ── */}
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                color: PRIMARY,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Personal Details
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {/* Row 1: Full Name · Mobile · Relation to Customer */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  required
                  size="small"
                  fullWidth
                  label="Full Name"
                  value={nominee.name}
                  onChange={(e) =>
                    handleChange("name", e.target.value.toUpperCase())
                  }
                  error={errors.name}
                  helperText={errors.name ? "Name is required" : ""}
                />
                <TextField
                  required
                  size="small"
                  fullWidth
                  label="Mobile"
                  {...mobileFieldProps()}
                  value={nominee.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                  error={errors.mobile}
                  helperText={errors.mobile ? "Mobile is required" : ""}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Relation to Customer"
                  placeholder="e.g. Son, Spouse, Friend"
                  value={nominee.relation}
                  onChange={(e) => handleChange("relation", e.target.value)}
                />
              </Stack>

              {/* Row 2: Date of Birth · Address · Note */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={nominee.dob}
                  onChange={(e) => handleChange("dob", e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  label="Address"
                  value={nominee.pickupAddress}
                  onChange={(e) =>
                    handleChange("pickupAddress", e.target.value)
                  }
                />
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  label="Note"
                  value={nominee.note}
                  onChange={(e) => handleChange("note", e.target.value)}
                />
              </Stack>
            </Stack>
          </Box>

          <Divider />

          {/* ── Nominee Documents ── */}
          <NomineeDocuments
            documents={nominee.documents}
            onChange={(docs) =>
              setNominee((prev) => ({ ...prev, documents: docs }))
            }
            nomineeMobile={nominee.mobile}
            prefix={`nom-dialog-`}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: PRIMARY },
            minWidth: 140,
          }}
        >
          {mode === "create" ? "Add Nominee" : "Save Nominee"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NomineeDialog;
